import React, { useState } from 'react';
import {
  Sparkles,
  Bot,
  X,
  Zap,
  Activity,
  ShieldCheck
} from 'lucide-react';

export const AskRisaWidget = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* Floating Trigger Button (Bottom-Right) */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 select-none">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={`group flex items-center gap-2.5 px-4 py-2.5 rounded-full text-white font-medium text-xs sm:text-sm shadow-xl transition-all duration-300 cursor-pointer border border-white/25 active:scale-95 ${
            isOpen
              ? 'bg-blue-800 shadow-blue-900/40 ring-4 ring-blue-500/20'
              : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-700 hover:to-indigo-800 shadow-blue-600/30 hover:shadow-blue-600/50 hover:scale-[1.03]'
          }`}
          title="Ask RISA AI"
          aria-label="Ask RISA AI"
        >
          <div className="relative flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-sky-200 group-hover:rotate-12 transition-transform duration-300" />
            <span className="absolute -top-1 -right-1 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-300 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-400" />
            </span>
          </div>

          <span className="tracking-tight font-semibold">Ask RISA</span>

          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-extrabold bg-white/20 text-sky-100 uppercase tracking-wider">
            AI
          </span>
        </button>
      </div>

      {/* Elegant Coming Soon Card Modal/Flyout */}
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Ask RISA - Coming Soon"
          className="fixed bottom-16 sm:bottom-20 right-3 sm:right-6 z-50 w-[340px] sm:w-[370px] max-w-[calc(100vw-1.5rem)] rounded-3xl shadow-2xl bg-white/95 backdrop-blur-2xl border border-blue-100/90 p-6 sm:p-7 text-center overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          {/* Top blue gradient accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-500" />

          {/* Close Button */}
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="absolute top-3.5 right-3.5 p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100/80 transition-colors cursor-pointer"
            title="Close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          {/* AI Avatar Hero with glowing pulse dot */}
          <div className="relative mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100/80 border border-blue-200/90 flex items-center justify-center text-blue-600 mb-4 shadow-md mt-1">
            <Bot className="w-8 h-8 text-blue-600 transform transition-transform duration-300 hover:scale-105" />

            <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-blue-600 border-2 border-white shadow-xs" />
            </span>
          </div>

          {/* Badge: Ask RISA AI */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200/80 mb-2 shadow-2xs">
            <Sparkles className="w-3.5 h-3.5 text-blue-600" />
            <span>Ask RISA • AI Assistant</span>
          </div>

          {/* Main Title */}
          <h3 className="text-xl font-bold text-gray-900 tracking-tight mb-2">
            Coming Soon
          </h3>

          {/* Clean Description */}
          <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-5">
            RISA AI is currently in active development. Soon, you will be able to ask anything and get instant real-time answers for ambulance dispatch, hospital beds, and routes.
          </p>

          {/* Feature Highlights (3 neat blue cards) */}
          <div className="space-y-2 mb-6 text-left">
            <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-blue-50/70 border border-blue-100/80 text-xs text-blue-900 font-medium">
              <div className="w-5 h-5 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                <Zap className="w-3 h-3 text-blue-600" />
              </div>
              <span className="truncate">Instant Ambulance Availability & ETAs</span>
            </div>

            <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-blue-50/70 border border-blue-100/80 text-xs text-blue-900 font-medium">
              <div className="w-5 h-5 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                <Activity className="w-3 h-3 text-blue-600" />
              </div>
              <span className="truncate">Live Hospital ICU Bed Tracking</span>
            </div>

            <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-blue-50/70 border border-blue-100/80 text-xs text-blue-900 font-medium">
              <div className="w-5 h-5 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-3 h-3 text-blue-600" />
              </div>
              <span className="truncate">Smart Operations & Dispatch Guidance</span>
            </div>
          </div>

          {/* Action / Dismiss Button */}
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold text-xs sm:text-sm shadow-md shadow-blue-500/20 transition-all cursor-pointer active:scale-98"
          >
            Got It
          </button>

          {/* Subtext */}
          <p className="text-[10px] text-gray-400 text-center mt-3">
            RISA Smart Ambulance Intelligence Engine
          </p>
        </div>
      )}
    </>
  );
};

export default AskRisaWidget;
